import { FormInstance } from 'antd';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { FORMAT_DATE_FOR_SAVE } from '../../const';
import { changeItemsForMoving } from '../../gantChangeData';
import { RootState } from '../../store';
import { DraggableGantType, ItemType, MappingItemIdIndexType } from '../../types';

const useControlStartEndDate = (
    form: FormInstance<ItemType>,
    values: ItemType,
    bufferItems: React.MutableRefObject<ItemType[]>,
    saveBufferToStore: () => void,
    updatedItem?: ItemType,
) => {
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);

    const controlStartEndDate = (item: ItemType) => {
        const startDate = values.startDate as any;
        const endDate = values.endDate as any;

        if (!startDate || !endDate) {
            return itemsList;
        }

        const getDiffDays = (start: string, end: string) => {
            const oldStart = DateTime.fromISO(start);
            const newStart = DateTime.fromISO(end);
            return Math.ceil(newStart.diff(oldStart, 'days').days);
        };

        const diffStartDay = getDiffDays(item.startDate, dayjs(startDate).format(FORMAT_DATE_FOR_SAVE));
        const diffEndDay = getDiffDays(item.endDate, dayjs(endDate).format(FORMAT_DATE_FOR_SAVE));

        const getDragging = (): DraggableGantType => {
            if (diffStartDay && !diffEndDay) return 'left';
            if (!diffStartDay && diffEndDay) return 'right';
            return 'both';
        };

        const dragging = getDragging();
        const diffDay = dragging === 'right' ? diffEndDay : diffStartDay;

        if (diffDay) {
            const previousStartDate = dayjs(values?.startDate);
            const previousEndDate = dayjs(values?.endDate);

            const diff = previousEndDate.diff(previousStartDate, 'day');

            const newStartDate =
                dragging === 'right' && diff < 0
                    ? previousEndDate.format(FORMAT_DATE_FOR_SAVE)
                    : previousStartDate.format(FORMAT_DATE_FOR_SAVE);
            const newEndDate =
                dragging === 'left' && diff < 0
                    ? previousStartDate.format(FORMAT_DATE_FOR_SAVE)
                    : previousEndDate.format(FORMAT_DATE_FOR_SAVE);

            if (diff < 0) {
                form.setFieldsValue({
                    startDate: dayjs(newStartDate) as any,
                    endDate: dayjs(newEndDate) as any,
                });
            }

            return changeItemsForMoving(
                mappingItemIdIndex,
                diffDay,
                item.children?.length ? 'both' : dragging,
                item,
                itemsList,
                [],
            );
        }

        return itemsList;
    };

    useEffect(() => {
        if (!updatedItem || !values || !values?.startDate || !values?.endDate) {
            return;
        }

        const isEqualStarDate = dayjs(values.startDate).format(FORMAT_DATE_FOR_SAVE) === updatedItem?.startDate;
        const isEqualEndDate = dayjs(values.endDate).format(FORMAT_DATE_FOR_SAVE) === updatedItem?.endDate;

        if (isEqualStarDate && isEqualEndDate) {
            return;
        }

        bufferItems.current = controlStartEndDate(updatedItem) || [];
        saveBufferToStore();
    }, [values?.startDate, values?.endDate]);

    return [];
};

export default useControlStartEndDate;
